import { ChatBubble, ContentCopy, Edit } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import InfoBlock from '~/components/infoBlock'
import { TInfoButton } from '~/components/infoBlock/infoButton'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys, EChatStatus } from '~/features/agents/models'
import useIsAdmin from '~/features/campaigns/hooks/useIsAdmin'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { getChatUrl } from '~/features/partners/utils'
import { ECapability } from '~/features/user/models'
import useCapabilities from '~/hooks/useCapabilities'
import { EClientPath } from '~/models/api'

type TAgentChatInfoProps = { isExpanded: boolean }

function AgentChatInfo({ isExpanded }: TAgentChatInfoProps) {
  const navigate = useNavigate()
  const { selectedAgent: agent } = useGetAgentCentralized()
  const { partner } = usePartnerCentralized()

  const agentId = agent?.[EAgentKeys.id] || ''
  const agentChatFriendlyUrl = agent?.[EAgentKeys.chatFriendlyUrl]
  const isChatEnabled = agent?.[EAgentKeys.chatStatus] === EChatStatus.active
  const isChatPaused = agent?.[EAgentKeys.chatStatus] === EChatStatus.paused
  const isAdmin = useIsAdmin()
  const { canEdit } = useCapabilities()
  const canEditAiAssistantConfig = canEdit(ECapability.aiAssistantConfig)
  const chatUrl = getChatUrl({
    agentChatFriendlyUrl,
    agentId,
    partner,
  })
  let message = chatUrl
  if (!isChatEnabled) {
    message = isChatPaused ? 'Chatbot is paused' : 'Activate chatbot'
  }

  const handleEditChat = () => {
    navigate(EClientPath.settingsChatbot)
  }

  const handleOpenChat = () => {
    window.open(chatUrl, '_blank')
  }

  const handleOpenChatAdmin = () => {
    const testChatUrl = `${chatUrl}?istesting=true`
    window.open(testChatUrl, '_blank')
  }

  const activeActions: TInfoButton[] = [
    {
      icon: <ContentCopy />,
      label: 'Copy',
      variant: 'outlined',
      textToCopy: chatUrl,
    },
    ...(canEditAiAssistantConfig
      ? [
          {
            icon: <Edit />,
            label: 'Edit',
            variant: 'outlined' as const,
            onClick: handleEditChat,
          },
        ]
      : []),
    {
      label: 'Chat',
      variant: 'contained',
      onClick: handleOpenChat,
    },
    ...(isAdmin
      ? [
          {
            label: 'Admin Test',
            variant: 'contained' as const,
            onClick: handleOpenChatAdmin,
          },
        ]
      : []),
  ]

  const disabledActions: TInfoButton[] = canEditAiAssistantConfig
    ? [
        {
          icon: <Edit />,
          label: 'Edit',
          variant: 'outlined',
          onClick: handleEditChat,
        },
      ]
    : []

  const actions = isChatEnabled ? activeActions : disabledActions

  let onLabelClick: (() => void) | undefined
  if (isChatEnabled) {
    onLabelClick = handleOpenChat
  } else if (canEditAiAssistantConfig) {
    onLabelClick = handleEditChat
  }

  return (
    <InfoBlock
      icon={<ChatBubble />}
      infoText={
        <>
          <p>How to use your AI assistant chat link</p>
          <ul style={{ margin: 0, paddingLeft: 16, paddingBottom: 16 }}>
            <li>
              First customize your chatbot, changing colors, adding a custom
              friendly URL and more
            </li>
            <li>Embed the chat widget on your website.</li>
            <li>
              Add the chat URL as your Link in bio to Instagram, Tiktok,
              Facebook, X, and other
            </li>
            <li>
              Print a QR code to the chat link on signage, receipts and more.
            </li>
          </ul>
        </>
      }
      title="Chat"
      value={message}
      isExpanded={isExpanded}
      actionButtons={actions}
      onLabelClick={onLabelClick}
    />
  )
}

export default AgentChatInfo
